import {
  useGetInventoryPackageFilters,
  useGetPackages,
  useLoadMoreProjectsByPackage
} from "@/api/useInventory"
import type { InventoryFiltersResponse } from "@/api/useInventory.types"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { Config, useHttp, type Params } from "@/hooks/useHttp"
import { useInitialRender } from "@/hooks/useInitialRender"
import { useTranslation } from "@/hooks/useTranslation"
import { getCountParams } from "@/organisms/Drawers/hooks/useCounts"
import { Filters } from "@/organisms/SmartSearch"
import { FilterServiceNS } from "@/organisms/SmartSearch/service/FilterService/FilterService.types"
import {
  getFilters,
  transformApiOptionsToFilterServiceFormat
} from "@/pages/DiscoveryPage/application/sbom-inventory/useSbomInventory.selector"
import { useRef } from "react"
import { getPackages, getProjectsByPackage } from "./useSbomInventory.selector"

export const useSbomInventory = (
  params: Params<Record<string, any>>,
  config?: { enabled: boolean; disableCountRequest?: boolean }
) => {
  const { disableCountRequest, ...restConfig } = config || {}

  const context = useInitialRender(
    useGetPackages(params, {
      select: getPackages,
      ...restConfig
    })
  )

  const { resourceId } = params || {}

  const contextCount = useGetPackages(
    resourceId ? { resourceId, ...getCountParams() } : getCountParams(),
    { enabled: !disableCountRequest && (config?.enabled ?? true) }
  )

  return [context, contextCount] as const
}

export const useProjectsByPackage = (
  params: Params<{ needle: string }>,
  config?: any
) => {
  const context = useLoadMoreProjectsByPackage(params, {
    select: getProjectsByPackage,
    ...config
  })

  return context
}

export const UsePackageReferencesCount = (params: Params, config: any = {}) => {
  return useHttp<{ data: number }>(
    RESOURCES_NAMES.INVENTORIES.PACKAGE_REFERENCES_COUNT,
    params,
    config
  )
}

export const useFiltersSbom = (config?: Config<InventoryFiltersResponse>) => {
  const { t } = useTranslation()

  const staticFilters = useRef<Filters>()
  if (!staticFilters.current) {
    staticFilters.current = getFilters({ t })
  }
  const { response: filterOptionsData, isLoading } =
    useGetInventoryPackageFilters<
      InventoryFiltersResponse,
      Record<string, FilterServiceNS.Options[]>
    >({}, { ...config, select: transformApiOptionsToFilterServiceFormat })

  return { filters: staticFilters.current, filterOptionsData, isLoading }
}
